import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../api';
import type { KnowledgeChunk, QaItem } from '../types';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { SortableHeader, type SortDirection } from '../components/SortableHeader';

/**
 * Knowledge Center - a unified workspace for the three content layers that drive
 * the bot's answers: the RAG FAQ knowledge base, the funnel's base questions
 * (prompts), and the per-step reply banks. A single global search filters the
 * active tab and each tab header shows how many matches it holds so the operator
 * can jump straight to the relevant layer, then edit / add / remove entries.
 */

type SubTab = 'rag' | 'funnel' | 'replies';

/** Hebrew display labels for conversation states (falls back to the raw key). */
const STATE_LABELS: Record<string, string> = {
  GREETING: 'פתיח / ברכה',
  WAITING_FOR_CAR_NUMBER: 'מספר רכב',
  WAITING_FOR_CAR_CONFIRMATION: 'אישור פרטי רכב',
  WAITING_FOR_ID: 'תעודת זהות',
  WAITING_FOR_AGE: 'גיל הנהג הצעיר',
  WAITING_FOR_GENDER: 'מין הנהג',
  WAITING_FOR_LICENSE_YEARS: 'ותק רישיון',
  WAITING_FOR_START_DATE: 'תאריך תחילת ביטוח',
  WAITING_FOR_COVERAGE_TYPE: 'סוג כיסוי',
  WAITING_FOR_DRIVERS_COUNT: 'מספר נהגים',
  WAITING_FOR_INSURANCE_CONTINUITY: 'רציפות ביטוח',
  WAITING_FOR_PRIOR_COVERAGE: 'כיסוי קודם',
  WAITING_FOR_INSURANCE_CLAIMS: 'תביעות ביטוח',
  WAITING_FOR_CITY: 'עיר מגורים',
  WAITING_FOR_FINAL_DETAILS: 'פרטים אחרונים',
  WAITING_FOR_CLAIMS_HISTORY: 'היסטוריית תביעות',
  READY_FOR_QUOTE: 'מוכן להצעת מחיר',
  QUOTE_SENT: 'הצעת מחיר נשלחה',
  COMPLETED: 'הושלם',
  HUMAN_HANDOFF: 'העברה לנציג',
};

const stateLabel = (state: string) => STATE_LABELS[state] ?? state;

/** Split a stored RAG chunk into its question (## heading) and answer body. */
function splitQA(text: string): { question: string; answer: string } {
  const trimmed = (text ?? '').trim();
  const lines = trimmed.split('\n');
  if (lines[0]?.startsWith('##')) {
    return {
      question: lines[0].replace(/^#+\s*/, '').trim(),
      answer: lines.slice(1).join('\n').trim(),
    };
  }
  return { question: '', answer: trimmed };
}

/** Recombine an edited question + answer into the stored `## Q\nA` format. */
function joinQA(question: string, answer: string): string {
  const q = question.trim();
  const a = answer.trim();
  if (q) return `## ${q}\n${a}`.trim();
  return a;
}

const includesQ = (haystack: string, needle: string) =>
  haystack.toLowerCase().includes(needle.toLowerCase());

// ── Shared modal shell ───────────────────────────────────────────────────────
function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose} aria-label="סגור">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-footer">{footer}</div>
      </div>
    </div>
  );
}

export function KnowledgeCenter({ tenantId }: { tenantId?: string }) {
  const [tab, setTab] = useState<SubTab>('rag');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  // RAG state
  const [chunks, setChunks] = useState<KnowledgeChunk[]>([]);
  // Q&A (bot-qa) state: the agency's own questions + replies.
  const [questions, setQuestions] = useState<QaItem[] | null>(null);
  const [replies, setReplies] = useState<QaItem[] | null>(null);
  // Unsaved local prompt edits, keyed by state -> { he?, en? }.
  const [promptEdits, setPromptEdits] = useState<Record<string, { he?: string; en?: string }>>({});
  const [promptDirty, setPromptDirty] = useState(false);
  const [resyncing, setResyncing] = useState(false);

  const refreshChunks = useCallback(() => {
    api
      .listKnowledge(tenantId)
      .then(setChunks)
      .catch((e) => setError(String(e)));
  }, [tenantId]);

  const refreshQa = useCallback(() => {
    api.listQaItems('question', tenantId).then(setQuestions).catch((e) => setError(String(e)));
    api.listQaItems('reply', tenantId).then(setReplies).catch((e) => setError(String(e)));
  }, [tenantId]);

  useEffect(() => {
    setSearch('');
    setPromptDirty(false);
    setPromptEdits({});
    refreshChunks();
    refreshQa();
  }, [tenantId, refreshChunks, refreshQa]);

  const pullLatestDefaults = async () => {
    if (!window.confirm('למשוך את ברירות המחדל העדכניות? עריכות מותאמות של הסוכנות יישמרו.')) return;
    setResyncing(true);
    try {
      const r = await api.resyncDefaults(tenantId);
      setPromptEdits({});
      setPromptDirty(false);
      refreshChunks();
      refreshQa();
      window.alert(`עודכן: ${r.added} נוספו, ${r.updated} עודכנו, ${r.removed} הוסרו, ${r.skipped} נשמרו.`);
    } catch (e) {
      setError(String(e));
    } finally {
      setResyncing(false);
    }
  };

  // ── Derived / filtered data ────────────────────────────────────────────────
  const filteredChunks = useMemo(() => {
    if (!search.trim()) return chunks;
    return chunks.filter(
      (c) => includesQ(c.text, search) || includesQ(c.source ?? '', search),
    );
  }, [chunks, search]);

  // Prompt text by state, from the QA question rows, with unsaved edits overlaid.
  const promptsByState = useMemo(() => {
    const m: Record<string, { he?: string; en?: string }> = {};
    for (const q of questions ?? []) {
      if (!q.state || (q.lang !== 'he' && q.lang !== 'en')) continue;
      m[q.state] = { ...m[q.state], [q.lang]: q.text ?? '' };
    }
    for (const [state, val] of Object.entries(promptEdits)) {
      m[state] = { ...m[state], ...val };
    }
    return m;
  }, [questions, promptEdits]);

  const promptEntries = useMemo(() => Object.entries(promptsByState), [promptsByState]);
  const filteredPrompts = useMemo(() => {
    if (!search.trim()) return promptEntries;
    return promptEntries.filter(
      ([state, val]) =>
        includesQ(state, search) ||
        includesQ(stateLabel(state), search) ||
        includesQ(val.he ?? '', search) ||
        includesQ(val.en ?? '', search),
    );
  }, [promptEntries, search]);

  // Reply rows grouped by state and split into default (read-only) vs custom.
  const { defaultBanks, customBanks, replyStates } = useMemo(() => {
    const def: Record<string, QaItem[]> = {};
    const cus: Record<string, QaItem[]> = {};
    for (const r of replies ?? []) {
      const bucket = r.origin === 'custom' ? cus : def;
      (bucket[r.state ?? ''] ??= []).push(r);
    }
    const states = Array.from(new Set([...Object.keys(def), ...Object.keys(cus)]));
    return { defaultBanks: def, customBanks: cus, replyStates: states };
  }, [replies]);

  const replyMatchCount = useMemo(() => {
    let count = 0;
    for (const state of replyStates) {
      const pairs = [...(defaultBanks[state] ?? []), ...(customBanks[state] ?? [])];
      for (const p of pairs) {
        if (
          !search.trim() ||
          includesQ(p.user ?? '', search) ||
          includesQ(p.bot ?? '', search) ||
          includesQ(stateLabel(state), search)
        )
          count += 1;
      }
    }
    return count;
  }, [replyStates, defaultBanks, customBanks, search]);

  const counts = {
    rag: filteredChunks.length,
    funnel: filteredPrompts.length,
    replies: replyMatchCount,
  };

  // ── Prompt (funnel question) mutators - persisted to the bot-qa table ───────
  const setPrompt = (state: string, lang: 'he' | 'en', value: string) => {
    setPromptEdits((edits) => ({ ...edits, [state]: { ...edits[state], [lang]: value } }));
    setPromptDirty(true);
  };

  const savePrompts = async () => {
    try {
      for (const [state, val] of Object.entries(promptEdits)) {
        for (const lang of ['he', 'en'] as const) {
          const text = val[lang];
          if (typeof text === 'string') await api.upsertQaQuestion(state, lang, text, tenantId);
        }
      }
      setPromptEdits({});
      setPromptDirty(false);
      refreshQa();
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="editor knowledge-center">
      <div className="kc-header">
        <div>
          <h2 style={{ marginBottom: 4 }}>מרכז הידע והתגובות</h2>
          <p className="page-subtitle" style={{ margin: 0 }}>
            חיפוש, עריכה והוספה של שאלות הבסיס, מאגר הידע (RAG) ובנק התגובות של הבוט
          </p>
        </div>
        <div className="search-box kc-search">
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            className="leads-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="חיפוש בכל השכבות…"
          />
        </div>
        <button
          className="btn outline"
          onClick={pullLatestDefaults}
          disabled={resyncing}
          title="מושך את ברירות המחדל העדכניות מבלי לדרוס עריכות מותאמות"
        >
          {resyncing ? 'מושך…' : 'משוך ברירות מחדל'}
        </button>
      </div>

      <div className="kc-tabs">
        {(
          [
            { id: 'rag', label: 'שאלות ותשובות (מאגר ידע)' },
            { id: 'funnel', label: 'שאלות הבסיס של הבוט' },
            { id: 'replies', label: 'בנק תגובות' },
          ] as { id: SubTab; label: string }[]
        ).map((t) => (
          <button
            key={t.id}
            className={`kc-tab${tab === t.id ? ' active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <span>{t.label}</span>
            <span className="count-badge">{counts[t.id]}</span>
          </button>
        ))}
      </div>

      {error && <div className="kc-error">שגיאה: {error}</div>}

      {tab === 'rag' && (
        <RagTab
          chunks={filteredChunks}
          allSources={Array.from(new Set(chunks.map((c) => c.source ?? 'dashboard')))}
          onAdd={async (text, source) => {
            await api.addKnowledge(text, source, tenantId);
            refreshChunks();
          }}
          onUpdate={async (id, text, source) => {
            await api.updateKnowledge(id, text, source, tenantId);
            refreshChunks();
          }}
          onDelete={async (id) => {
            await api.deleteKnowledge(id, tenantId);
            refreshChunks();
          }}
        />
      )}

      {tab === 'funnel' && (
        <FunnelTab
          loading={questions === null}
          prompts={filteredPrompts}
          dirty={promptDirty}
          onChange={setPrompt}
          onSave={savePrompts}
        />
      )}

      {tab === 'replies' && (
        <RepliesTab
          loading={replies === null}
          states={replyStates}
          defaultBanks={defaultBanks}
          customBanks={customBanks}
          search={search}
          onAddPair={async (state, user, bot) => {
            await api.createQaReply(state, user, bot, tenantId);
            refreshQa();
          }}
          onUpdatePair={async (id, state, user, bot) => {
            await api.updateQaReply(id, state, user, bot, tenantId);
            refreshQa();
          }}
          onDeletePair={async (id) => {
            await api.deleteQaItem(id, tenantId);
            refreshQa();
          }}
        />
      )}
    </div>
  );
}

// ── RAG tab ──────────────────────────────────────────────────────────────────
interface RagDraft {
  id?: string;
  question: string;
  answer: string;
  source: string;
}

function RagTab({
  chunks,
  allSources,
  onAdd,
  onUpdate,
  onDelete,
}: {
  chunks: KnowledgeChunk[];
  allSources: string[];
  onAdd: (text: string, source: string | undefined) => Promise<void>;
  onUpdate: (id: string, text: string, source: string | undefined) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const [sourceFilter, setSourceFilter] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>(null);
  const [draft, setDraft] = useState<RagDraft | null>(null);
  const [busy, setBusy] = useState(false);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      if (sortDir === 'asc') setSortDir('desc');
      else if (sortDir === 'desc') {
        setSortDir(null);
        setSortKey(null);
      } else setSortDir('asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const rows = useMemo(() => {
    let list = chunks.map((c) => ({ chunk: c, ...splitQA(c.text) }));
    if (sourceFilter.length > 0) {
      list = list.filter((r) => sourceFilter.includes(r.chunk.source ?? 'dashboard'));
    }
    if (sortKey && sortDir) {
      const dir = sortDir === 'asc' ? 1 : -1;
      list = [...list].sort((a, b) => {
        if (sortKey === 'length') {
          return (a.chunk.text.length - b.chunk.text.length) * dir;
        }
        const av = sortKey === 'source' ? a.chunk.source ?? '' : a.question || a.answer;
        const bv = sortKey === 'source' ? b.chunk.source ?? '' : b.question || b.answer;
        return av.localeCompare(bv, 'he') * dir;
      });
    }
    return list;
  }, [chunks, sourceFilter, sortKey, sortDir]);

  const save = async () => {
    if (!draft) return;
    const text = joinQA(draft.question, draft.answer);
    if (!text.trim()) return;
    setBusy(true);
    try {
      const source = draft.source.trim() || undefined;
      if (draft.id) await onUpdate(draft.id, text, source);
      else await onAdd(text, source);
      setDraft(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kc-panel">
      <div className="kc-toolbar">
        <button className="btn" onClick={() => setDraft({ question: '', answer: '', source: 'dashboard' })}>
          + הוסף שאלה ותשובה
        </button>
        <div className="filter-group">
          <span className="flabel">מקור:</span>
          <MultiSelectFilter
            options={allSources.map((s) => ({ value: s, label: s }))}
            selected={sourceFilter}
            onChange={setSourceFilter}
            searchPlaceholder="חפש מקור…"
          />
        </div>
        <div className="spacer" />
        <div className="kc-sort">
          <span className="flabel">מיין:</span>
          <SortableHeader label="שאלה" sortKey="question" currentSort={sortKey} currentDirection={sortDir} onSort={handleSort} />
          <SortableHeader label="מקור" sortKey="source" currentSort={sortKey} currentDirection={sortDir} onSort={handleSort} />
          <SortableHeader label="אורך" sortKey="length" currentSort={sortKey} currentDirection={sortDir} onSort={handleSort} />
        </div>
      </div>

      {rows.length === 0 && <div className="empty">לא נמצאו ערכים</div>}

      {rows.map(({ chunk, question, answer }) => (
        <div key={chunk.id} className="card kc-qa">
          <div className="kc-qa-top">
            <span className="badge">{chunk.source || 'dashboard'}</span>
            <div className="row-actions">
              <button
                className="btn outline"
                onClick={() => setDraft({ id: chunk.id, question, answer, source: chunk.source ?? 'dashboard' })}
              >
                ערוך
              </button>
              <button
                className="btn danger"
                onClick={() => {
                  if (window.confirm('למחוק את הערך הזה מהמאגר?')) onDelete(chunk.id);
                }}
              >
                מחק
              </button>
            </div>
          </div>
          {question && <p className="kc-question">{question}</p>}
          <p className="kc-answer">{answer}</p>
        </div>
      ))}

      {draft && (
        <Modal
          title={draft.id ? 'עריכת ערך במאגר' : 'הוספת שאלה ותשובה'}
          onClose={() => setDraft(null)}
          footer={
            <>
              <button className="btn" disabled={busy} onClick={save}>
                שמירה
              </button>
              <button className="btn ghost" onClick={() => setDraft(null)}>
                ביטול
              </button>
            </>
          }
        >
          <div className="field">
            <label>שאלה</label>
            <input value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })} placeholder="לדוגמה: מה נחשב לתביעה?" />
          </div>
          <div className="field">
            <label>תשובה</label>
            <textarea rows={6} value={draft.answer} onChange={(e) => setDraft({ ...draft, answer: e.target.value })} />
          </div>
          <div className="field">
            <label>מקור (אופציונלי)</label>
            <input value={draft.source} onChange={(e) => setDraft({ ...draft, source: e.target.value })} />
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Funnel prompts tab ───────────────────────────────────────────────────────
function FunnelTab({
  loading,
  prompts,
  dirty,
  onChange,
  onSave,
}: {
  loading: boolean;
  prompts: [string, { he?: string; en?: string }][];
  dirty: boolean;
  onChange: (state: string, lang: 'he' | 'en', value: string) => void;
  onSave: () => void;
}) {
  if (loading) return <div className="empty">טוען…</div>;

  return (
    <div className="kc-panel">
      {prompts.length === 0 && <div className="empty">לא נמצאו שאלות</div>}
      {prompts.map(([state, val]) => (
        <div key={state} className="card kc-prompt">
          <div className="kc-prompt-head">
            <strong>{stateLabel(state)}</strong>
            <span className="kc-state-id">{state}</span>
          </div>
          <div className="field">
            <label>נוסח בעברית</label>
            <textarea rows={2} value={val.he ?? ''} onChange={(e) => onChange(state, 'he', e.target.value)} />
          </div>
          <div className="field">
            <label>נוסח באנגלית</label>
            <textarea rows={2} value={val.en ?? ''} onChange={(e) => onChange(state, 'en', e.target.value)} />
          </div>
        </div>
      ))}

      {dirty && (
        <div className="kc-savebar">
          <span className="badge">יש שינויים שלא נשמרו</span>
          <button className="btn" onClick={onSave}>
            שמור שינויים
          </button>
        </div>
      )}
    </div>
  );
}

// ── Reply banks tab ──────────────────────────────────────────────────────────
interface PairDraft {
  state: string;
  id?: string;
  user: string;
  bot: string;
  busy?: boolean;
}

function RepliesTab({
  loading,
  states,
  defaultBanks,
  customBanks,
  search,
  onAddPair,
  onUpdatePair,
  onDeletePair,
}: {
  loading: boolean;
  states: string[];
  defaultBanks: Record<string, QaItem[]>;
  customBanks: Record<string, QaItem[]>;
  search: string;
  onAddPair: (state: string, user: string, bot: string) => Promise<void>;
  onUpdatePair: (id: string, state: string, user: string, bot: string) => Promise<void>;
  onDeletePair: (id: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState<PairDraft | null>(null);

  if (loading) return <div className="empty">טוען…</div>;

  const matches = (p: QaItem, state: string) =>
    !search.trim() ||
    includesQ(p.user ?? '', search) ||
    includesQ(p.bot ?? '', search) ||
    includesQ(stateLabel(state), search);

  const visibleStates = states.filter((state) => {
    const def = (defaultBanks[state] ?? []).filter((p) => matches(p, state));
    const custom = (customBanks[state] ?? []).filter((p) => matches(p, state));
    return def.length > 0 || custom.length > 0 || !search.trim();
  });

  const submit = async () => {
    if (!draft || !draft.user.trim() || !draft.bot.trim()) return;
    setDraft({ ...draft, busy: true });
    try {
      if (draft.id) await onUpdatePair(draft.id, draft.state, draft.user.trim(), draft.bot.trim());
      else await onAddPair(draft.state, draft.user.trim(), draft.bot.trim());
      setDraft(null);
    } catch {
      setDraft((d) => (d ? { ...d, busy: false } : d));
    }
  };

  return (
    <div className="kc-panel">
      <p className="page-subtitle" style={{ marginTop: 0 }}>
        תגובות ברירת המחדל הן לקריאה בלבד (ניתן לעדכן אותן דרך "משוך ברירות מחדל"). ניתן להוסיף ולערוך תגובות מותאמות לכל שלב.
      </p>

      {visibleStates.length === 0 && <div className="empty">לא נמצאו תגובות</div>}

      {visibleStates.map((state) => {
        const def = (defaultBanks[state] ?? []).filter((p) => matches(p, state));
        const custom = (customBanks[state] ?? []).filter((p) => matches(p, state));
        return (
          <div key={state} className="card kc-bank">
            <div className="kc-prompt-head">
              <strong>{stateLabel(state)}</strong>
              <span className="kc-state-id">{state}</span>
              <div className="spacer" />
              <button className="btn outline" onClick={() => setDraft({ state, user: '', bot: '' })}>
                + תגובה מותאמת
              </button>
            </div>

            {def.map((p) => (
              <div key={p.id} className="kc-pair">
                <span className="badge kc-builtin">ברירת מחדל</span>
                <div className="kc-pair-body">
                  <div className="kc-trigger">{p.user}</div>
                  <div className="kc-reply">{p.bot}</div>
                </div>
              </div>
            ))}

            {custom.map((p) => (
              <div key={p.id} className="kc-pair kc-pair-custom">
                <span className="badge kc-custom">מותאם</span>
                <div className="kc-pair-body">
                  <div className="kc-trigger">{p.user}</div>
                  <div className="kc-reply">{p.bot}</div>
                </div>
                <div className="row-actions">
                  <button
                    className="btn outline"
                    onClick={() => setDraft({ state, id: p.id, user: p.user ?? '', bot: p.bot ?? '' })}
                  >
                    ערוך
                  </button>
                  <button
                    className="btn danger"
                    onClick={() => {
                      if (window.confirm('למחוק את התגובה המותאמת הזו?')) void onDeletePair(p.id);
                    }}
                  >
                    מחק
                  </button>
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {draft && (
        <Modal
          title={draft.id ? 'עריכת תגובה מותאמת' : 'הוספת תגובה מותאמת'}
          onClose={() => setDraft(null)}
          footer={
            <>
              <button
                className="btn"
                disabled={!draft.user.trim() || !draft.bot.trim() || draft.busy}
                onClick={submit}
              >
                אישור
              </button>
              <button className="btn ghost" onClick={() => setDraft(null)}>
                ביטול
              </button>
            </>
          }
        >
          <div className="field">
            <label>שלב</label>
            <input value={stateLabel(draft.state)} disabled />
          </div>
          <div className="field">
            <label>טריגר (מה הלקוח עשוי לכתוב)</label>
            <input value={draft.user} onChange={(e) => setDraft({ ...draft, user: e.target.value })} />
          </div>
          <div className="field">
            <label>תגובת הבוט</label>
            <textarea rows={4} value={draft.bot} onChange={(e) => setDraft({ ...draft, bot: e.target.value })} />
          </div>
        </Modal>
      )}
    </div>
  );
}
